import { apiErrorBodies } from "./apiErrorBodies";

/**
 * Extracts the machine-readable error code from a Tactic API error.
 *
 * Every error the API returns is `{ error: { code, message } }`. The message is
 * written for a developer; the code is the stable half, so branching on it —
 * to show a specific, actionable message — is safer than matching on prose.
 *
 * Mirrors the shapes {@link getApiErrorMessage} understands: an
 * `ApiClientError` (`error.response.result`), a `ResponseWithStatus` passed
 * directly (`error.result`), or the result object on its own.
 *
 * @param error - The error object from an API call or catch block
 * @returns The error code, or `undefined` when the error carries none
 *
 * @example
 * ```ts
 * onError: (error) => {
 *   const code = getApiErrorCode(error);
 *   toast.error(
 *     code === "office_not_allowed"
 *       ? "That request type isn't available in every selected office."
 *       : getApiErrorMessage(error, "Failed to save")
 *   );
 * }
 * ```
 */
function extractCode(result: Record<string, unknown>): string | undefined {
  if (result.error && typeof result.error === "object") {
    const errorObj = result.error as Record<string, unknown>;
    if (typeof errorObj.code === "string" && errorObj.code) {
      return errorObj.code;
    }
  }
  // Some endpoints flatten the envelope to `{ code, message }`.
  if (typeof result.code === "string" && result.code) {
    return result.code;
  }
  return undefined;
}

export function getApiErrorCode(error: unknown): string | undefined {
  for (const body of apiErrorBodies(error)) {
    const code = extractCode(body);
    if (code) return code;
  }
  return undefined;
}
